import {React, useEffect, useState} from "react";
import Joi from "joi"
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {
    Checkbox,
    DateInput,
    ExtraCurricularRadioGroup,
    Input,
    RadioGroup,
    Select,
    TextArea
} from "../../elements/forms/Inputs"
import {FormSectionContent} from "../../elements/forms/FormLayout"
import Form, {baseHandleChange, baseHandleDateChange, baseHandleListChange} from "../../elements/forms/Form"
import {useNavigate, useParams} from "react-router-dom";
import {
    addRegistrationStudentPhoto, confirmRegistration,
    getChoices,
    getExtraCurriculars, getInvoicePDF, getRegistration,
    getRegistrationWithSecret,
    getStudentData, getSummerSchoolCourses,
    patchRegistration,
    postRegistration, refreshInvoiceLines, sendInvoice
} from "../../../api/summer_school";
import {
    AddButton,
    DangerButton,
    DownloadButton,
    PrimaryButton,
    SecondaryButton, SuccessButton,
    UpdateButton
} from "../../elements/Buttons";
import {toast} from "react-toastify";
import {getMyStudentsOrAll} from "../../../api/school";
import {useAuth} from "../../../contexts/AuthContext";
import {DeleteButton} from "../../elements/DeleteButton";
import countryOptions from "../../../utils/countries";
import {validate} from "../../../utils/validation";
import ConfirmationPage from "./ConfirmationPage";
import checkPermission from "../../../utils/permissions";
import {getStudierzeitRooms} from "../../../api/organizational";
import {formatCurrency, formatDate} from "../../../utils/formatters";
import * as PropTypes from "prop-types";
import {getMyTeacherCoursesExcel} from "../../../api/courses";

UpdateButton.propTypes = {
    label: PropTypes.string,
    onClick: PropTypes.func
};
export default function RegistrationCreateEdit() {

    const {permissions, currentUserId} = useAuth()
    const {id, secret} = useParams()
    const [data, setData] = useState({})
    const [helperData, setHelperData] = useState({})
    const [errors, setErrors] = useState({}) // validation errors

    const totalSteps = 12;
    const totalStepsTeam = 14;
    const [currentStep, setCurrentStep] = useState(1)
    const [stepError, setStepError] = useState()
    const [furthestVisitedStep, setFurthestVisitedStep] = useState(1)

    const [selectedFile, setSelectedFile] = useState();
    const [isFilePicked, setIsFilePicked] = useState(false);

    const [parentErrors, setParentErrors] = useState([])
    const [newParentRowId, setNewParentRowId] = useState(1)

    const [invoiceLineErrors, setInvoiceLineErrors] = useState([])
    const [newInvoiceLineRowId, setNewInvoiceLineRowId] = useState(1)

    const {data: choices} = useQuery(['summer_school_choices'], getChoices, {refetchInterval: 1000 * 60 * 60 * 24})
    const {data: extraCurriculars} = useQuery(['summer_school_extra_curriculars'], getExtraCurriculars, {refetchInterval: 1000 * 60 * 60 * 24})
    const {data: rooms} = useQuery(["studierzeit_rooms"], getStudierzeitRooms, {
        refetchInterval: 1000 * 60 * 60 * 24,
        enabled: !!currentUserId
    })
    const {data: courses} = useQuery(["summer_school_courses"], getSummerSchoolCourses, {
        refetchInterval: 1000 * 60 * 60 * 24,
        enabled: !!currentUserId
    })

    const studentStatusOptions = [
        {value: 'KPI', label: `${data.first_name} ist bereits am Kurpfalz Internat`},
        {value: 'NEU', label: `${data.first_name} wird zum neuen Schuljahr an das Kurpfalz Internat wechseln`},
        {
            value: 'PROSPECT',
            label: `${data.first_name} möchte das Kurpfalz Internat kennenlernen und später entscheiden, ob ein Besuch in Frage kommt`
        },
        {value: 'SUMMER_SCHOOL_ONLY', label: `${data.first_name} möchte nur die Summer School besuchen`}
    ]

    const grades = Array.from({length: 8}, (_, i) => {
        const grade = i + 5;
        return {value: grade, label: `${grade}. Klasse`};
    })

    const {data: studentData, isSuccess: studentDataLoaded} = useQuery(
        ['summer_school_student_data', helperData?.student?.id],
        () => getStudentData(helperData?.student?.id),
        {
            enabled: !!helperData.student
        })

    const {data: students} = useQuery(
        ['my_students_or_all'],
        getMyStudentsOrAll, {
            refetchInterval: 1000 * 60 * 60 * 24,
            enabled: !!currentUserId
        })

    let navigate = useNavigate();

    useEffect(() => {
        loadData()
    }, [id, secret])

    // Initialize furthest visited step when data is loaded
    useEffect(() => {
        if (data.id) {
            // If we have data, set furthest visited step to at least the current step
            if (currentStep > furthestVisitedStep) {
                setFurthestVisitedStep(currentStep)
            }
        }
    }, [data.id, currentStep, furthestVisitedStep])

    // effect to update the data fields once studentData is loaded
    useEffect(() => {
        if (studentDataLoaded) {

            const parents = studentData.parents.map((p, index) => {
                return {
                    id: p.id,
                    first_name: p.first_name,
                    last_name: p.last_name,
                    appellation: p.appellation,
                    role: p.role,
                    country: p.country,
                    street: p.street,
                    zip_code: p.zip_code,
                    city: p.city,
                    mobile: p.mobile,
                    email: p.email,
                    is_main_registrant: index === 0
                }
            })

            setData({
                ...data,
                first_name: studentData.first_name,
                last_name: studentData.last_name,
                birth_date: studentData.birth_date,
                gender: studentData.gender,
                grade: studentData.grade_number,
                mobile: studentData.mobile,
                email: studentData.email,
                health_insurance: studentData.health_insurance,
                medications: studentData.medications,
                illness: studentData.illness,
                student_status: 'KPI',
                school_state: 'Baden-Württemberg',
                school_name: 'Kurpfalz Internat',
                school_type: studentData.school_type.name === 'Gymnasium' ? 'Gymnasium' : 'Realschule',
                parents: parents
            })
        }
    }, [studentData])

    // add a parent row if not yet and we're on step 2
    useEffect(() => {
        if (!data || !Array.isArray(data.parents)) return
        if (currentStep === 2 && !data.parents?.length) {
            addParentRow()
        }
    }, [currentStep, data])

    // Add second parent when first parent's responsibility is set to "gemeinsam erziehungsberechtigt"
    useEffect(() => {
        if (!data || !Array.isArray(data.parents) || !choices?.parent_responsibility) return

        // Only check for the first parent (index 0)
        if (data.parents.length === 1) {
            const firstParent = data.parents[0]

            // Find the option that matches "gemeinsam erziehungsberechtigt"
            const sharedResponsibilityOption = choices.parent_responsibility.find(
                option => option.label === "gemeinsam erziehungsberechtigt"
            )

            if (sharedResponsibilityOption && firstParent.responsibility === sharedResponsibilityOption.value) {
                // Add a second parent with the same responsibility
                const newSecondParent = {
                    id: `N${newParentRowId}`,
                    country: "Deutschland",
                    is_main_registrant: false,
                    responsibility: sharedResponsibilityOption.value
                }
                setNewParentRowId(newParentRowId + 1)
                setParents([...data.parents, newSecondParent])
            }
        }
    }, [data.parents, choices?.parent_responsibility, newParentRowId])

    async function loadData() {

        if (!id) return

        let newData

        if (id && !secret && currentUserId) {
            newData = await getRegistration(id)
        } else {
            newData = await getRegistrationWithSecret(id, secret)
        }

        setData(newData)
    }

    // Define schema for each step
    const schema = {
        step1: {
            first_name: Joi.string().required(),
            last_name: Joi.string().required(),
            birth_date: Joi.date().required(),
            gender: Joi.string().required(),
            grade: Joi.number().required(),
            email: Joi.string().allow(null, ''),
            student_status: Joi.string().required(),
        },
        step2: {
            parents: Joi.array().min(1).items(
                Joi.object({
                    first_name: Joi.string().required().messages({
                        "string.empty": "Vorname ist erforderlich",
                        "any.required": "Vorname ist erforderlich"
                    }),
                    last_name: Joi.string().required().messages({
                        "string.empty": "Nachname ist erforderlich",
                        "any.required": "Nachname ist erforderlich"
                    }),
                    email: Joi.string().required().messages({
                        "string.empty": "E-Mail ist erforderlich",
                        "string.email": "Bitte geben Sie eine gültige E-Mail-Adresse ein",
                        "any.required": "E-Mail ist erforderlich"
                    }),
                    mobile: Joi.string().required().messages({
                        "string.empty": "Handynummer ist erforderlich",
                        "any.required": "Handynummer ist erforderlich"
                    }),
                    street: Joi.string().required().messages({
                        "string.empty": "Straße ist erforderlich",
                        "any.required": "Straße ist erforderlich"
                    }),
                    zip_code: Joi.string().required().messages({
                        "string.empty": "PLZ ist erforderlich",
                        "any.required": "PLZ ist erforderlich"
                    }),
                    city: Joi.string().required().messages({
                        "string.empty": "Ort ist erforderlich",
                        "any.required": "Ort ist erforderlich"
                    }),
                    country: Joi.string().required().messages({
                        "string.empty": "Land ist erforderlich",
                        "any.required": "Land ist erforderlich"
                    }),
                })
            ).messages({
                "array.min": "Mindestens ein Elternteil ist erforderlich",
                "any.required": "Elternteil ist erforderlich"
            }),
        },
        step3: {
            school_name: Joi.string().required(),
            school_state: Joi.string().required(),
            school_type: Joi.string().required(),
            moved_to_next_grade: Joi.string().required(),
            // extra_examination: Joi.string().required(),
            requested_subject: Joi.object().required(),
            subject_grade: Joi.string().required(),
            subject_remarks: Joi.string().required(),
            request_alternative_subject: Joi.boolean().required(),
        },
        step4: {
            // Optional step for KPI students
        },
        step5: {
            extra_curricular: Joi.object().required(),
        },
        step6: {
            diet: Joi.string().required(),
            dietary_restrictions_yes_no: Joi.boolean().required(),
            dietary_restrictions: Joi.when('dietary_restrictions_yes_no', {
                is: true,
                then: Joi.string().required(),
                otherwise: Joi.optional()
            }),
            illness_yes_no: Joi.boolean().required(),
            illness: Joi.when('illness_yes_no', {
                is: true,
                then: Joi.string().required(),
                otherwise: Joi.optional()
            }),
            health_restrictions_yes_no: Joi.boolean().required(),
            health_restrictions: Joi.when('health_restrictions_yes_no', {
                is: true,
                then: Joi.string().required(),
                otherwise: Joi.optional()
            }),
            medication_yes_no: Joi.boolean().required(),
            medication: Joi.when('medication_yes_no', {
                is: true,
                then: Joi.string().required(),
                otherwise: Joi.optional()
            }),
            psychological_treatment: Joi.boolean().required(),
            psychological_treatment_remarks: Joi.when('psychological_treatment', {
                is: true,
                then: Joi.string().required(),
                otherwise: Joi.optional()
            }),
            insurance_type: Joi.string().required(),
            insurance: Joi.string().required(),
            insurance_via_parents: Joi.string().required(),
            permission_to_see_doctor: Joi.boolean().required()
        },
        step7: {
            pocket_money: Joi.number().required(),
        },
        step8: {
            swimming_permission: Joi.boolean().required(),
            leave_premises_permission: Joi.boolean().required()
        },
        step9: {
            attended_summer_school_before: Joi.any().required(),
        },
        step10: {
            contract_terms: Joi.boolean().required().valid(true).messages({
                "any.only": "Bitte bestätigen Sie Ihr Einverständnis",
            }),
            travel_insurance: Joi.boolean().required().valid(true).messages({
                "any.only": "Bitte bestätigen Sie Ihr Einverständnis",
            }),
            application_terms: Joi.boolean().required().valid(true).messages({
                "any.only": "Bitte bestätigen Sie Ihr Einverständnis",
            }),
        },
    };

    const canEdit = checkPermission(permissions, "summer_school.change_registration") || data?.status === 'DRAFT' || data?.status === 'PENDING_CONFIRMATION'
    const isTeamEditing = checkPermission(permissions, "summer_school.change_registration")

    const title = "Summer School Anmeldung"
    const successMessage = (currentStep < totalSteps - 1) ? "Anmeldung gespeichert" : "Anmeldung abgesendet"
    const totalAvailableSteps = isTeamEditing ? totalStepsTeam : totalSteps

    async function onSave() {
        // Validate the current step before saving
        if (!validateCurrentStep()) {
            setStepError("Bitte füllen Sie alle erforderlichen Felder aus")
            // Scroll to the top to show the error message
            window.scrollTo(0, 0);
            throw new Error("Validation failed");
        }

        // Clear any step errors on successful validation
        setStepError(null);

        let newParents = data.parents?.filter(t => {
            // remove parents completely if they start with N and we're not on step 2
            return !(t.id.toString().startsWith("N") && currentStep !== 2);
        }).map(t => {
            if (t.id.toString().startsWith("N")) {
                return {...t, id: null}
            }
            return {...t}
        })

        let newInvoiceLines = data.invoice_lines?.map(t => {
            if (t.id.toString().startsWith("N")) {
                return {...t, id: null}
            }
            return {...t}
        })

        const tmpdata = {...data, parents: newParents, invoice_lines: newInvoiceLines}
        console.log(tmpdata)

        if (currentStep === totalSteps - 1) {
            tmpdata.status = 'PENDING_CONFIRMATION'
        }

        try {
            let newData
            if (!tmpdata.id) {
                newData = await postRegistration(tmpdata, currentUserId)

                if (currentUserId) navigate(`/summer-school/registrations/${newData.id}/${newData.secret}/edit`)
                else navigate(`/summer-school/registration/${newData.id}/${newData.secret}/edit`)
            } else newData = await patchRegistration(tmpdata, currentUserId)
            setData(newData)

            // then add the file
            if (isFilePicked) {
                const formData = new FormData();
                formData.append('file', selectedFile);
                newData = await addRegistrationStudentPhoto(formData, newData.id, newData.secret, currentUserId)
                setData(newData)
            }

            // final step: go to confirmation page
            if (currentStep === totalSteps - 1) {
                setCurrentStep(totalSteps)
            }
            toast.success(successMessage)

        } catch (e) {
            console.log(e)
            if (e.message !== "Validation failed") { // Don't show API error for validation failures
                toast.error("Fehler beim Speichern. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.")
            }
            throw e
        }

    }

    async function onSetStatus(status) {
        if (!isTeamEditing) return
        try {
            let tmpData = {...data, status: status}
            const newData = await patchRegistration(tmpData, currentUserId)
            setData(newData)
            toast.success(successMessage)
        } catch (e) {
            console.log(e)
            if (e.message !== "Validation failed") { // Don't show API error for validation failures
                toast.error("Fehler beim Speichern. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.")
            }
            throw e
        }
    }

    async function onSendInvoice() {

        try {
            await sendInvoice(data.id)
            toast.success("Rechnung versendet.")

        } catch (e) {
            console.log(e)
            if (e.message !== "Validation failed") { // Don't show API error for validation failures
                toast.error("Fehler beim Versenden.")
            }
            throw e
        }
    }

    async function onRefreshInvoiceLines() {
        const newData = await refreshInvoiceLines(data.id)
        setData(newData)
    }

    function handleChange(evt) {
        baseHandleChange(evt, data, setData, errors, setErrors, schema)
    }

    function handleHelperChange(evt) {
        baseHandleChange(evt, helperData, setHelperData, errors, setErrors, schema)
    }

    function handleDateChange(date, path) {
        baseHandleDateChange(date, path, data, setData, errors, setErrors, schema, false)
    }

    const handleChangeFile = (event) => {
        const file = event.target.files[0]

        if (file) {
            // Check file type
            const fileType = file.type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png']

            if (!validTypes.includes(fileType)) {
                toast.error('Bitte wählen Sie eine JPG, JPEG oder PNG Datei aus.')
                event.target.value = null
                return
            }

            // Check file size (5MB = 5 * 1024 * 1024 bytes)
            const maxSize = 5 * 1024 * 1024

            if (file.size > maxSize) {
                toast.error('Die Datei ist zu groß. Maximale Größe: 5 MB')
                event.target.value = null
                return
            }

            setSelectedFile(file)
            setIsFilePicked(true)
        }
    }

    /* Parents */
    function setParents(newParents) {
        setData({...data, parents: newParents})
    }

    function addParentRow() {
        const newRow = {
            id: `N${newParentRowId}`,
            country: "Deutschland",
            is_main_registrant: data.parents?.length === 0
        }
        setNewParentRowId(newParentRowId + 1)
        setParents([...data.parents, newRow])
    }

    async function handleParentDelete(parentId) {
        const newParents = [...data.parents.filter(e => e.id !== parentId)]
        setParents(newParents)
    }

    function handleParentListChange(evt, rowId) {
        baseHandleListChange(rowId, evt, data.parents, setParents, parentErrors, setParentErrors, {})
    }

    /* Invoice Lines */
    function setInvoiceLines(newInvoiceLines) {
        setData({...data, invoice_lines: newInvoiceLines})
    }

    function addInvoiceLineRow() {
        const newRow = {
            id: `N${newInvoiceLineRowId}`,
            quantity: 1,
            line_type: 'Sonstiges'
        }
        setNewInvoiceLineRowId(newInvoiceLineRowId + 1)
        setInvoiceLines([...data.invoice_lines, newRow])
    }

    async function handleInvoiceLineDelete(invoiceLineId) {
        const newInvoiceLines = [...data.invoice_lines.filter(e => e.id !== invoiceLineId)]
        setInvoiceLines(newInvoiceLines)
    }

    function handleInvoiceLineListChange(evt, rowId) {
        baseHandleListChange(rowId, evt, data.invoice_lines, setInvoiceLines, invoiceLineErrors, setInvoiceLineErrors, {})
    }

    async function nextStep() {

        try {
            await onSave()
        } catch (e) {
            console.log(e)
            return
        }

        if (currentStep === totalAvailableSteps) return

        let nextStepNumber;


        if (currentStep === 3 && data.student_status !== 'KPI' && data.student_status !== 'NEU') {
            // Skip step 4 unless student_status is KPI or NEU
            nextStepNumber = 5; // Skip to step 5
        } else if (currentStep === 11 && isTeamEditing) {
            // Skip step 12 if team editing
            nextStepNumber = 13
        } else {
            nextStepNumber = currentStep + 1;
        }

        // Update furthest visited step if we're going further than before
        if (nextStepNumber > furthestVisitedStep) {
            setFurthestVisitedStep(nextStepNumber);
        }

        setCurrentStep(nextStepNumber);
        window.scrollTo(0, 0);
    }

    function validateCurrentStep() {
        // Get the schema for the current step
        const currentStepSchema = schema[`step${currentStep}`];

        // If there's no schema for this step, return true (no validation needed)
        if (!currentStepSchema) return true;

        // Special handling for step 2 (parents validation)
        if (currentStep === 2) {
            return validateParentsStep();
        }

        // Validate the data against the schema for the current step
        const validationErrors = validate(currentStepSchema, data);

        // If there are validation errors, update the errors state and return false
        if (validationErrors) {
            setErrors(validationErrors);
            return false;
        }

        // If there are no validation errors, clear the errors state and return true
        setErrors({});
        return true;
    }

    function validateParentsStep() {
        // Check if parents array exists and has at least one entry
        if (!data.parents || data.parents.length === 0) {
            setErrors({parents: "Mindestens ein Elternteil ist erforderlich"});
            return false;
        }

        // Validate each parent
        let hasErrors = false;
        const newParentErrors = [];

        data.parents.forEach((parent, index) => {
            const parentErrors = {};

            // Check required fields for each parent
            if (!parent.first_name) {
                parentErrors.first_name = "Vorname ist erforderlich";
                hasErrors = true;
            }

            if (!parent.last_name) {
                parentErrors.last_name = "Nachname ist erforderlich";
                hasErrors = true;
            }

            if (!parent.email) {
                parentErrors.email = "E-Mail ist erforderlich";
                hasErrors = true;
            } else if (!/^\S+@\S+\.\S+$/.test(parent.email)) {
                parentErrors.email = "Bitte geben Sie eine gültige E-Mail-Adresse ein";
                hasErrors = true;
            }

            if (!parent.mobile) {
                parentErrors.mobile = "Handynummer ist erforderlich";
                hasErrors = true;
            }

            if (!parent.street) {
                parentErrors.street = "Straße ist erforderlich";
                hasErrors = true;
            }

            if (!parent.zip_code) {
                parentErrors.zip_code = "PLZ ist erforderlich";
                hasErrors = true;
            }

            if (!parent.city) {
                parentErrors.city = "Ort ist erforderlich";
                hasErrors = true;
            }

            if (!parent.country) {
                parentErrors.country = "Land ist erforderlich";
                hasErrors = true;
            }

            // Add errors for this parent if any
            if (Object.keys(parentErrors).length > 0) {
                newParentErrors.push({
                    id: parent.id,
                    errors: parentErrors
                });
            }
        });

        if (hasErrors) {
            setParentErrors(newParentErrors);
            return false;
        }

        // Clear parent errors if no issues
        setParentErrors([]);
        return true;
    }

    function previousStep() {
        const previousStep = currentStep - 1
        if (previousStep >= 1) {
            setCurrentStep(previousStep)
        }
    }

    // Function to handle navigation clicks
    async function handleNavigationClick(step) {
        // Don't allow navigation to future steps beyond the furthest visited step
        if (step > furthestVisitedStep && !isTeamEditing) {
            return
        }
        setCurrentStep(step)
    }

    // Helper function to determine the class for navigation items
    function getNavItemClass(step) {
        if (currentStep === step) {
            return "text-imsblue-600 font-medium cursor-pointer"
        } else if (step <= furthestVisitedStep || isTeamEditing) {
            // Steps that have been visited or are accessible (up to furthest visited step)
            return "text-gray-700 cursor-pointer"
        } else {
            // Future steps beyond the furthest visited step
            return "text-gray-400"
        }
    }

    if (data.id && !canEdit) navigate(`/summer-school/registration/${data.id}/${data.secret}/confirm`)

    return (

        <div className="max-w-5xl mx-auto px-4 py-4 sm:px-6 md:px-8">

            <div className="mx-auto grid grid-cols-1 gap-6 md:grid-flow-col-dense md:grid-cols-4">

                <div className="md:col-span-1 pt-14 pr-2">
                    {/* Left column - Form navigation */}
                    <nav className="space-y-1">
                        <ul className="list-disc pl-5 space-y-2">
                            <li
                                className={getNavItemClass(1)}
                                onClick={() => handleNavigationClick(1)}
                            >
                                SchülerIn
                            </li>
                            <li
                                className={getNavItemClass(2)}
                                onClick={() => handleNavigationClick(2)}
                            >
                                Erziehungsberechtigte
                            </li>
                            <li
                                className={getNavItemClass(3)}
                                onClick={() => handleNavigationClick(3)}
                            >
                                Schule & Kursfach
                            </li>
                            {(data.student_status === 'KPI' || data.student_status === 'NEU') && (
                                <li
                                    className={getNavItemClass(4)}
                                    onClick={() => handleNavigationClick(4)}
                                >
                                    KPI Informationen
                                </li>
                            )}
                            <li
                                className={getNavItemClass(5)}
                                onClick={() => handleNavigationClick(5)}
                            >
                                Nachmittagsprogramm
                            </li>
                            <li
                                className={getNavItemClass(6)}
                                onClick={() => handleNavigationClick(6)}
                            >
                                Gesundheit
                            </li>
                            <li
                                className={getNavItemClass(7)}
                                onClick={() => handleNavigationClick(7)}
                            >
                                Taschengeld
                            </li>
                            <li
                                className={getNavItemClass(8)}
                                onClick={() => handleNavigationClick(8)}
                            >
                                Erlaubnisse
                            </li>
                            <li
                                className={getNavItemClass(9)}
                                onClick={() => handleNavigationClick(9)}
                            >
                                Frühere Teilnahme
                            </li>
                            <li
                                className={getNavItemClass(10)}
                                onClick={() => handleNavigationClick(10)}
                            >
                                Einverständniserklärung
                            </li>
                            <li
                                className={getNavItemClass(11)}
                                onClick={() => handleNavigationClick(11)}
                            >
                                {isTeamEditing ? "Übersicht" : "Überprüfen"}
                            </li>
                            {!isTeamEditing && <li
                                className={getNavItemClass(12)}
                                onClick={() => handleNavigationClick(12)}
                            >
                                Bestätigung
                            </li>}

                            {isTeamEditing && <li
                                className={getNavItemClass(13)}
                                onClick={() => handleNavigationClick(13)}
                            >
                                Verwaltung
                            </li>}
                            {isTeamEditing && <li
                                className={getNavItemClass(14)}
                                onClick={() => handleNavigationClick(14)}
                            >
                                Rechnung
                            </li>}
                        </ul>
                    </nav>
                </div>

                <div className="md:col-span-3">
                    {/* right column */}
                    <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
                    {stepError && (
                        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg"
                                         viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd"
                                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{stepError}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <Form id="summerSchoolApplication" onSave={onSave}
                          onDelete={null}
                          data={data} setData={setData}
                          errors={errors} setErrors={setErrors}
                          hideSubmitButton={true}
                          schema={schema}>

                        {currentStep === 1 &&
                            <FormSectionContent>

                                <h5 className="sm:col-span-6 font-medium">SchülerIn</h5>
                                <p className="text-sm font-medium text-gray-900 sm:col-span-6">
                                    Hiermit beantrage ich die Aufnahme unserer Tochter bzw. unseres Sohnes in die Summer
                                    School des Privaten Kurpfalz-Internats Helge Lehmann KG:</p>

                                {students && !data.id && <Select path="student"
                                                                 label="Schüler"
                                                                 className="sm:col-span-6"
                                                                 options={students}
                                                                 valueAttr="id"
                                                                 labelAttr="full_name"
                                                                 onChange={handleHelperChange}
                                                                 errors={errors}
                                                                 data={helperData}
                                                                 isClearable={false}
                                                                 valueOnly={false}
                                />}

                                <Input
                                    path="first_name"
                                    label="Vorname"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />
                                <Input
                                    path="last_name"
                                    label="Nachname"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />

                                <DateInput
                                    path="birth_date"
                                    label="Geburtsdatum"
                                    className="sm:col-span-3"
                                    onChange={handleDateChange}
                                    errors={errors}
                                    data={data}
                                />
                                <Select
                                    path="gender"
                                    label="Geschlecht"
                                    className="sm:col-span-3"
                                    options={choices?.genders}
                                    valueAttr="value"
                                    labelAttr="label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={true}
                                />

                                <Select
                                    path="grade"
                                    label="Klasse am 31.05."
                                    className="sm:col-span-3"
                                    options={grades}
                                    valueAttr="value"
                                    labelAttr="label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={true}
                                />

                                <Select
                                    path="school_type"
                                    label="Art der Schule"
                                    className="sm:col-span-3"
                                    options={choices?.school_types}
                                    valueAttr="value"
                                    labelAttr="label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={true}
                                />

                                {data.school_type === 'andere Schulform' &&
                                    <TextArea
                                        path="school_type_remarks"
                                        label="Bitte beschreiben Sie die Art der Schulform"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        rows={4}
                                        className="sm:col-span-6"
                                        helpText=""
                                    />}

                                <Input
                                    path="mobile"
                                    label="Handynummer Schüler"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />
                                <Input
                                    path="email"
                                    label="E-Mail Schüler"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />

                                <Select
                                    path="tshirt_size"
                                    label="T-Shirt-Größe"
                                    className="sm:col-span-3"
                                    options={choices?.tshirt_sizes}
                                    valueAttr="value"
                                    labelAttr="label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={true}
                                />
                                <Input
                                    path="height"
                                    label="Körpergröße"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />

                                <div className="sm:col-span-6 text-sm">
                                    <p className="text-sm font-medium text-gray-900 mb-1">Foto des Schülers (kann nachgereicht werden)</p>
                                    <input
                                        type="file"
                                        name="student_photo"
                                        accept=".jpg,.jpeg,.png"
                                        onChange={handleChangeFile}
                                    /> <br/>
                                    <p className="text-xs text-gray-500 mt-1">JPG, JPEG, PNG Dateien. Maximale Größe: 5
                                        MB</p>
                                    <div>
                                        {data.photo && <p className="py-1">Aktuelle Datei: {data.photo}</p>}
                                    </div>
                                </div>

                                <Select
                                    path="student_status"
                                    label={`Ist ${data.first_name || "Ihr Kind"} Schülerin oder Schüler des Kurpfalz Internats?`}
                                    className="sm:col-span-6"
                                    options={studentStatusOptions}
                                    valueAttr="value"
                                    labelAttr="label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={true}
                                />

                            </FormSectionContent>}

                        {currentStep === 2 && <FormSectionContent>

                            <h5 className="sm:col-span-6 font-medium text-lg">Anmelder & Erziehungsberechtigte</h5>

                            <div className="sm:col-span-6 overflow-hidden bg-white shadow sm:rounded-md">
                                <ul role="list" className="divide-y divide-gray-200 space-y-2">
                                    {data.parents?.map((parent, index) => (
                                        <li key={parent.id}>
                                            <div className="block hover:bg-gray-50">
                                                <div className="px-4 py-4 sm:px-6">
                                                    <div className="">
                                                        <div className="gap-6 grid lg:grid-cols-6">

                                                            <h6 className="sm:col-span-6 font-medium text-md">Anmelder/Erziehungsberechtigter {index + 1}</h6>
                                                            <Select
                                                                path="appellation"
                                                                label="Anrede"
                                                                className="sm:col-span-2"
                                                                options={choices?.appellations}
                                                                valueAttr="value"
                                                                labelAttr="label"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                                isClearable={false}
                                                                valueOnly={true}
                                                            />

                                                            <Input
                                                                path="first_name"
                                                                type="text"
                                                                label="Vorname"
                                                                className="sm:col-span-2"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />
                                                            <Input
                                                                path="last_name"
                                                                type="text"
                                                                label="Nachname"
                                                                className="sm:col-span-2"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Select
                                                                path="role"
                                                                label="Rolle"
                                                                className="sm:col-span-3"
                                                                options={choices?.parent_roles}
                                                                valueAttr="value"
                                                                labelAttr="label"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                                isClearable={false}
                                                                valueOnly={true}
                                                            />

                                                            <Select
                                                                path="responsibility"
                                                                label="Erziehungsberechtigung"
                                                                className="sm:col-span-3"
                                                                options={choices?.parent_responsibility}
                                                                valueAttr="value"
                                                                labelAttr="label"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                                isClearable={false}
                                                                valueOnly={true}
                                                            />

                                                            <Input
                                                                path="email"
                                                                type="email"
                                                                label="E-Mail"
                                                                className="sm:col-span-3"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Input
                                                                path="mobile"
                                                                type="text"
                                                                label="Handynummer"
                                                                className="sm:col-span-3"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Input
                                                                path="street"
                                                                type="text"
                                                                label="Straße"
                                                                className="sm:col-span-6"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Input
                                                                path="zip_code"
                                                                type="text"
                                                                label="PLZ"
                                                                className="sm:col-span-2"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Input
                                                                path="city"
                                                                type="text"
                                                                label="Ort"
                                                                className="sm:col-span-2"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                            />

                                                            <Select
                                                                path="country"
                                                                label="Land"
                                                                className="sm:col-span-2"
                                                                options={countryOptions}
                                                                valueAttr="label"
                                                                labelAttr="label"
                                                                onChange={handleParentListChange}
                                                                errors={parentErrors}
                                                                data={data.parents}
                                                                rowId={parent.id}
                                                                isClearable={false}
                                                                valueOnly={true}
                                                            />
                                                        </div>

                                                    </div>

                                                    <div className="mt-2">
                                                        <DeleteButton
                                                            onDelete={() => handleParentDelete(parent.id)}/>
                                                    </div>

                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="my-4 ml-4 sm:col-span-6">
                                <AddButton showLabel={true} onClick={addParentRow}
                                           label="Weitere Kontaktperson hinzuüfgen"/>
                            </div>


                        </FormSectionContent>}

                        {currentStep === 3 && <FormSectionContent>

                            <h5 className="sm:col-span-6 font-medium">Informationen zur Schule</h5>

                            <Input
                                path="school_name"
                                label="Name der Schule"
                                className="sm:col-span-3"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                            />

                            <Select
                                path="school_state"
                                label="Bundesland"
                                className="sm:col-span-3"
                                options={choices?.school_states}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            <Select
                                path="moved_to_next_grade"
                                label={`Wird ${data.first_name} voraussichtlich versetzt?`}
                                className="sm:col-span-3"
                                options={choices?.moving_status}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            {/*<Select*/}
                            {/*    path="extra_examination"*/}
                            {/*    label={`Macht ${data.first_name} eine Nachprüfung?`}*/}
                            {/*    className="sm:col-span-3"*/}
                            {/*    options={choices?.extra_examination}*/}
                            {/*    valueAttr="value"*/}
                            {/*    labelAttr="label"*/}
                            {/*    onChange={handleChange}*/}
                            {/*    errors={errors}*/}
                            {/*    data={data}*/}
                            {/*    isClearable={false}*/}
                            {/*    valueOnly={true}*/}
                            {/*/>*/}

                            <Checkbox
                                path="uses_ipad_in_school"
                                label={`Benutzt ${data.first_name} im Unterricht ein iPad?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                hideLegend={true}
                            />

                            <h5 className="mt-4 sm:col-span-6 font-medium">Gewünschtes Fach</h5>

                            <p className="sm:col-span-6 text-sm">
                                Als Summer School Teilnehmer muss {data.first_name}
                                eines der Fächer <strong>Deutsch, Deutsch als Fremdsprache, Englisch, Französisch,
                                Latein, Mathematik</strong> oder <strong>Legsathenietraining</strong> auswählen.</p>
                            <p className="sm:col-span-6 text-sm">
                                In diesem Fach hat {data.first_name} täglich dreimal 40 Minuten Unterricht.
                                Hinzu kommen dreimal 40 Minuten Hausaufgaben in einer Studierzeit. Unterricht und
                                Hausaufgabenzeiten wechseln sich ab. Ergänzt wird unser Unterrichtsprogramm durch ein
                                Methodentraining, das („Vorname Schüler“) hilft, in Zukunft einfacher und effizienter zu
                                lernen.
                            </p>

                            <Select
                                path="requested_subject"
                                label={`Welches Kursfach möchte ${data.first_name} belegen?`}
                                className="sm:col-span-6"
                                options={choices?.subjects.filter(s => s.available_as_course === true)}
                                valueAttr="id"
                                labelAttr="name"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={false}
                            />
                            <Select
                                path="subject_grade"
                                label={`Wie ist die Note des letzten Zeugnisses/der letzten Halbjahresinformation im Kursfach?`}
                                className="sm:col-span-6"
                                options={choices?.school_grades}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />
                            {data.requested_subject?.is_foreign_language && <Select
                                path="started_foreign_language_in_grade"
                                label={`Wann hat ${data.first_name} mit dem Unterricht in der Fremdsprache begonnen (Grundschule nicht inbegriffen)?`}
                                className="sm:col-span-6"
                                options={grades}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />}
                            {data.requested_subject?.name === 'Deutsch als Fremdsprache' && <Select
                                path="gafl_level"
                                label={`Welches Sprachniveau hat ${data.first_name} bisher erreicht?`}
                                className="sm:col-span-6"
                                options={choices?.gafl_level}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />}

                            <TextArea
                                path="subject_remarks"
                                label={`Bitte beschreiben Sie die Schwierigkeiten, die ${data.first_name} im gewählten Kursfach hat:`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />

                            <Input
                                path="subject_book"
                                label={(data.requested_subject ? `Welches Schulbuch wird in ${data.requested_subject?.name} verwendet?` : `Welches Schulbuch wird im gewünschten Kursfach verwendet?`) + ' (Kann nachgereicht werden)'}
                                className="sm:col-span-3"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                            />

                            {/*Alternatives Fach */}
                            <h5 className="mt-4 -mb-4 sm:col-span-6 font-medium">Alternatives Fach</h5>

                            <RadioGroup
                                key={"request_alternative_subject"}
                                path="request_alternative_subject"
                                label={`Soll ${data.first_name} ein anderes Fach belegen, wenn der Kurs im gewünschten Fach nicht zustande kommt?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: true, label: "Ja, wir möchten Unterricht in folgendem Fach"},
                                    {value: false, label: "Nein, wir erbitten Ihre Absage"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                            />

                            {data.request_alternative_subject === true &&
                                <>
                                    <Select
                                        path="alternative_subject"
                                        label={`Welches Kursfach möchte ${data.first_name} als alternatives Fach belegen?`}
                                        className="sm:col-span-6"
                                        options={choices?.subjects.filter(s => s.available_as_course === true)}
                                        valueAttr="id"
                                        labelAttr="name"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        isClearable={false}
                                        valueOnly={false}
                                    />
                                    <Select
                                        path="alternative_subject_grade"
                                        label={`Wie ist die Note des letzten Zeugnisses/der letzten Halbjahresinformation im Kursfach?`}
                                        className="sm:col-span-6"
                                        options={choices?.school_grades}
                                        valueAttr="value"
                                        labelAttr="label"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        isClearable={false}
                                        valueOnly={true}
                                    />
                                    {data.alternative_subject?.is_foreign_language && <Select
                                        path="started_foreign_language_in_grade"
                                        label={`Wann hat ${data.first_name} mit dem Unterricht in der Fremdsprache begonnen (Grundschule nicht inbegriffen)?`}
                                        className="sm:col-span-6"
                                        options={grades}
                                        valueAttr="value"
                                        labelAttr="label"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        isClearable={false}
                                        valueOnly={true}
                                    />}
                                    {data.alternative_subject?.name === 'Deutsch als Fremdsprache' && <Select
                                        path="galternative_afl_level"
                                        label={`Welches Sprachniveau hat ${data.first_name} bisher erreicht?`}
                                        className="sm:col-span-6"
                                        options={choices?.gafl_level}
                                        valueAttr="value"
                                        labelAttr="label"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        isClearable={false}
                                        valueOnly={true}
                                    />}

                                    <TextArea
                                        path="alternative_subject_remarks"
                                        label={`Bitte beschreiben Sie die Schwierigkeiten, die ${data.first_name} im alternativen Kursfach hat:`}
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                        rows={4}
                                        className="sm:col-span-6"
                                        helpText=""
                                    />

                                    <Input
                                        path="alternative_subject_book"
                                        label={(data.alternative_subject ? `Welches Schulbuch wird in ${data.alternative_subject?.name} verwendet?` : `Welches Schulbuch wird im alternativen Kursfach verwendet?`) + ' (Kann nachgereicht werden)'}
                                        className="sm:col-span-3"
                                        onChange={handleChange}
                                        errors={errors}
                                        data={data}
                                    />
                                </>}
                        </FormSectionContent>}

                        {currentStep === 4 && <FormSectionContent>

                            <h5 className="mt-4 sm:col-span-6 font-medium">Zusätzliche Einzelstunde</h5>

                            <p className="sm:col-span-6 text-sm">
                                Schülerinnen und Schüler des Kurpfalz Internats können zusätzlich
                                zum Kursfach noch eine Einzelstunde in einem zweiten Fach belegen. In diesem Fall
                                reduziert sich
                                die Zahl der Studierzeitstunden von drei auf zwei Stunden. Die Kosten einer zusätzlichen
                                Einzelstunde betragen (Variable) € für den Kurszeitraum.
                            </p>

                            <Checkbox
                                path="book_extra_lesson"
                                label={`Ja, ${data.first_name} möchte eine zusätzliche Einzelstunde buchen`}
                                className="sm:col-span-3"
                                onChange={handleHelperChange}
                                errors={errors}
                                data={helperData}
                                hideLegend={true}
                            />

                            {helperData.book_extra_lesson === true && <Select
                                path="extra_lesson_subject"
                                label={`${data.first_name} möchte eine Einzelstunde in folgendem Fach buchen:`}
                                className="sm:col-span-6"
                                options={choices?.subjects.filter(s => s.available_as_extra_lesson === true)}
                                valueAttr="id"
                                labelAttr="name"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={false}
                            />}

                        </FormSectionContent>}


                        {currentStep === 5 && <FormSectionContent>

                            <h5 className="mt-4 sm:col-span-6 font-medium">Freizeitmodul</h5>

                            <p className="sm:col-span-6 text-sm">
                                Der Vormittag ist montags bis samstags für den Unterricht reserviert. Am Nachmittag und
                                am
                                Wochenende organisiert unser Betreuerteam ein Freizeitprogramm, das die Summer School zu
                                einem
                                unvergesslichen Erlebnis werden lässt.
                            </p>
                            <p className="sm:col-span-6 text-sm font-medium">
                                Im Nachmittagsprogramm muss {data.first_name} eine Nachmittagsaktivität wählen, die
                                jeweils
                                Montag, Dienstag, Donnerstag und Freitagnachmittag angeboten wird.
                            </p>
                            <p className="sm:col-span-6 text-sm font-medium">
                                Am Mittwoch, Samstag und Sonntag gibt es ein zusätzliches Freizeitangebot, dass für die
                                Klassen
                                5 bis 8 verpflichtend ist und für die Klassen 9 bis K1 Wahloptionen bietet.
                            </p>
                            <p className="sm:col-span-6 text-sm">
                                Aktuelle Infos zu den angebotenen Nachmittagsaktivitäten finden Sie unter <a
                                href="https://www.kurpfalz-internat.de" className="text-imsblue-600"
                                target={"_blank"}>www.kurpfalz-internat.de</a>
                            </p>

                            <ExtraCurricularRadioGroup
                                path="extra_curricular"
                                label={`${data.first_name} hat sich für folgendes Nachmittagsprogramm entschieden:`}
                                className="sm:col-span-6"
                                options={extraCurriculars}
                                valueAttr="id"
                                labelAttr="name"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                valueOnly={false}
                            />

                        </FormSectionContent>}

                        {currentStep === 6 && <FormSectionContent>
                            <h5 className="sm:col-span-6 font-medium">Angaben zur Gesundheit</h5>
                            <p className="sm:col-span-6 text-sm">
                                Es uns sehr wichtig, dass es {data.first_name} in der Summer School gut geht. Bitte
                                beantworten Sie uns daher einige Fragen zur Ernährung und Gesundheit:
                            </p>

                            <Select
                                path="diet"
                                label={`Wie ernährt sich ${data.first_name}?`}
                                className="sm:col-span-6"
                                options={choices?.diet}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            <RadioGroup
                                path="dietary_restrictions_yes_no"
                                label={`Hat ${data.first_name} Lebenmittelunverträglichkeiten oder -allergien?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Nein"},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                                tiles={false}
                            />


                            {data.dietary_restrictions_yes_no === true && <TextArea
                                path="dietary_restrictions"
                                label={`Welche besonderen Anforderungen bestehen an die Ernährung?`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />}

                            <h5 className="sm:col-span-6 font-medium mt-4">Krankheiten</h5>

                            <RadioGroup
                                path="illness_yes_no"
                                label={`Leidet ${data.first_name} an einer Allergie oder anderen (schwerwiegenden) Krankheiten:`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Nein"},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                                tiles={false}
                            />

                            {data.illness_yes_no === true && <TextArea
                                path="illness"
                                label={`Bitte nennen Sie die Krankheiten hier:`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />}

                            <RadioGroup
                                path="health_restrictions_yes_no"
                                label={`Hat ${data.first_name} besondere gesundheitliche Einschränkungen, die wir kennen müssen?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Nein"},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                                tiles={false}
                            />

                            {data.health_restrictions_yes_no === true && <TextArea
                                path="health_restrictions"
                                label={`Bitte beschreiben Sie die gesundheitlichen Einschränkungen:`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />}

                            <RadioGroup
                                path="medication_yes_no"
                                label={`Nimmt ${data.first_name} regelmäßig Medikamente?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Nein"},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                                tiles={false}
                            />

                            {data.medication_yes_no === true && <TextArea
                                path="medication"
                                label={`Bitte nennen Sie die Medikamente:`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />}

                            <RadioGroup
                                path="psychological_treatment"
                                label={`Hat ${data.first_name} eine psychische Erkrankung, die im Rahmen einer stationären oder ambulanten Therapie behandelt wird?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Nein"},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                                tiles={false}
                            />

                            {data.psychological_treatment === true && <TextArea
                                path="psychological_treatment_remarks"
                                label={`Bitte beschreiben Sie die Erkrankung:`}
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                rows={4}
                                className="sm:col-span-6"
                                helpText=""
                            />}

                            <h5 className="sm:col-span-6 font-medium mt-4">Versicherung</h5>

                            <Select
                                path="insurance_type"
                                label={`${data.first_name} ist wie folgt krankenversichert:`}
                                className="sm:col-span-6"
                                options={choices?.insurance_type}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            <Input
                                path="insurance"
                                label={`In welcher Krankenkasse ist ${data.first_name} versichert?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                            />

                            <Select
                                path="insurance_via_parents"
                                label={`Ist ${data.first_name} mit einem Elternteil mitversichert?`}
                                className="sm:col-span-6"
                                options={choices?.insurance_via_parents}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            <h5 className="sm:col-span-6 font-medium mt-4">Einverständnis Arztbesuch</h5>

                            <Checkbox
                                path="permission_to_see_doctor"
                                label={`Ich bin einverstanden, dass ${data.first_name} im Falle einer Erkrankung einen vom Kurpfalz-Internat empfohlenen Arzt aufsucht.`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                hideLegend={true}
                            />
                        </FormSectionContent>}

                        {currentStep === 7 && <FormSectionContent>

                            <h5 className="sm:col-span-6 font-medium">Taschengeld</h5>
                            <p className="sm:col-span-6 text-sm">
                                Während der Summer School führen wir für {data.first_name} zur Vermeidung von
                                Diebstählen ein Schülerkonto. Dieses Angebot ist freiwillig.
                            </p>
                            <p className="sm:col-span-6 text-sm">
                                {data.first_name} kann täglich Taschengeld abheben. Sie können das Taschengeld bei
                                Anreise bar einzahlen oder bereits mit der Rechnung der Summer School überweisen. Wir
                                empfehlen einen Betrag von maximal 120€. Bitte nennen Sie hier den Betrag, falls Sie mit
                                der Rechnung einzahlen möchten:
                            </p>
                            <Input
                                path="pocket_money"
                                type="number"
                                label={`Taschengeld €`}
                                className="sm:col-span-2"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                            />
                            <p className="sm:col-span-6 text-sm">
                                Nicht verwendetes Taschengeld wird {data.first_name} am letzten Kurstag ausgezahlt.
                            </p>
                        </FormSectionContent>}


                        {currentStep === 8 && <FormSectionContent>

                            <h5 className="sm:col-span-6 font-medium">Einverständniserklärung</h5>
                            <RadioGroup
                                path="leave_premises_permission"
                                label={`${data.first_name} darf sich während der Summer School in der Gemeinde Bammental frei bewegen. Sind Sie damit einverstanden?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: "Ja, ich bin einverstanden"},
                                    {
                                        value: true,
                                        label: `Nein, ich möchte, dass ${data.first_name} das Internat nur in Begleitung eines Erwachsenen verlässt.`
                                    }
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                            />

                            <RadioGroup
                                path="swimming_permission"
                                label={`Die Gemeinde Bammental hat ein bewachtes Waldschwimmbad, das ${data.first_name} selbständig mit anderen Schülerinnen und Schülern besuchen darf. Hierfür benötigen wir Ihre Genehmigung:`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {
                                        value: false,
                                        label: `${data.first_name} kann schwimmen und darf auf eigene Verantwortung das nahegelegene Waldschwimmbad in Bammental besuchen.`
                                    },
                                    {
                                        value: true,
                                        label: `${data.first_name} kann nicht sicher schwimmen und darf nicht unbegleitet ins Schwimmbad gehen.`
                                    }
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                            />

                        </FormSectionContent>}

                        {currentStep === 9 && <FormSectionContent>

                            <RadioGroup
                                path="attended_summer_school_before"
                                label={`Hat ${data.first_name} die Summer School schon einmal besucht?`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                options={[
                                    {value: false, label: `Nein`},
                                    {value: true, label: "Ja"}
                                ]}
                                valueAttr="value"
                                labelAttr="label"
                                valueOnly={true}
                            />

                            {data.attended_summer_school_before === true &&
                                <Input
                                    path="attended_year"
                                    label={`In welchem Jahr?`}
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />}


                            <Select
                                path="how_did_you_hear"
                                label={`Wie wurden Sie auf die Summer School aufmerksam?`}
                                className="sm:col-span-3"
                                options={choices?.how_did_you_hear}
                                valueAttr="value"
                                labelAttr="label"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                isClearable={false}
                                valueOnly={true}
                            />

                            {data.how_did_you_hear === 'Andere' &&
                                <Input
                                    path="how_did_you_hear_remarks"
                                    label={`Details`}
                                    className="sm:col-span-3 mt-5"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />
                            }

                            {data.attended_summer_school_before === false && data.how_did_you_hear === 'Empfehlung' &&
                                <Input
                                    path="recommended_by"
                                    label={`Wer hat Ihnen die Summer School empfohlen?`}
                                    className="sm:col-span-6"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />
                            }
                        </FormSectionContent>}


                        {currentStep === 10 && <FormSectionContent>
                            <Checkbox
                                path="contract_terms"
                                label={`Ich habe die Vertragsbedingungen zur Kenntnis genommen und erkenne sie an`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                hideLegend={true}
                            />
                            <Checkbox
                                path="travel_insurance"
                                label={`Ich nehme zur Kenntnis, dass ich selbständig eine Reiserücktrittsversicherung abschließen muss, da keine Kostenerstattung bei Nichtteilnahme erfolgt.`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                hideLegend={true}
                            />
                            <Checkbox
                                path="application_terms"
                                label={`Ich nehme zur Kenntnis, dass die Anmeldung erst rechtsverbindlich wird, wenn das Kurpfalz Internat die Anmeldung bestätigt.`}
                                className="sm:col-span-6"
                                onChange={handleChange}
                                errors={errors}
                                data={data}
                                hideLegend={true}
                            />
                        </FormSectionContent>}


                        {currentStep === 11 && <FormSectionContent>

                            {!isTeamEditing && <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Anmeldung überprüfen</h3>
                                <p className="mt-1 text-sm text-gray-700">
                                    Bitte überprüfen Sie Ihre Angaben vor dem Absenden der Anmeldung.
                                    Sie können jederzeit zurück zu einem Abschnitt gehen, um Änderungen vorzunehmen.
                                </p>
                            </div>}

                            <div className="sm:col-span-6">
                                <ConfirmationPage data={data}
                                                  choices={choices}
                                                  editingAllowed={true}
                                                  studentStatusOptions={studentStatusOptions}
                                                  goToStep={(step) => setCurrentStep(step)}/>
                            </div>
                        </FormSectionContent>}

                        {currentStep === totalSteps && <FormSectionContent>

                            <div className="sm:col-span-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Bestätigung ausstehend</h3>
                                <p className="mt-4 mb-12 text-sm text-gray-700">
                                    <strong>Nächster Schritt:</strong> Um Ihre E-Mail-Adresse zu verifizieren, haben wir Ihnen eine E -Mail mit einem
                                    Bestätigungslink zugesendet. Bitte öffnen Sie Ihr E-Mail Konto und klicken Sie auf den
                                    zugesendeten Link, um Ihre Anmeldung abzuschließen.
                                </p>
                            </div>

                            <div className="sm:col-span-6">
                                {(() => {
                                    const missingItems = [];

                                    // Check for missing student photo
                                    if (!data.photo) {
                                        missingItems.push({
                                            text: "Schülerfoto",
                                            step: 1,
                                            description: "Bitte ein aktuelles Foto Ihres Kindes hochladen"
                                        });
                                    }

                                    // Check for missing subject book
                                    if (!data.subject_book) {
                                        missingItems.push({
                                            text: "Schulbuch für Hauptfach",
                                            step: 3,
                                            description: `Schulbuch für ${data.requested_subject?.name || 'das gewählte Kursfach'}`
                                        });
                                    }

                                    // Check for missing alternative subject book (only if alternative subject is requested)
                                    if (data.request_alternative_subject && !data.alternative_subject_book) {
                                        missingItems.push({
                                            text: "Schulbuch für alternatives Fach",
                                            step: 3,
                                            description: `Schulbuch für ${data.alternative_subject?.name || 'das alternative Kursfach'}`
                                        });
                                    }

                                    if (missingItems.length === 0) {
                                        return null; // Don't show anything if all items are complete
                                    }

                                    return (
                                        <div>
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Folgende Informationen bitte nachtragen</h3>
                                            <div className="mt-4 mb-12">
                                                <ul className="space-y-2">
                                                    {missingItems.map((item, index) => (
                                                        <li key={index} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                                            <div>
                                                                <span className="text-sm font-medium text-yellow-800">{item.text}</span>
                                                                <p className="text-xs text-yellow-600">{item.description}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => setCurrentStep(item.step)}
                                                                className="ml-4 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                                            >
                                                                Bearbeiten
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    );
                                })()}

                            </div>
                        </FormSectionContent>}

                        {currentStep === 13 && <FormSectionContent>
                            <>
                                <Input
                                    path="room"
                                    label="Zimmer"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />

                                <Select
                                    path="study_hall"
                                    label="Studierzimmer"
                                    className="sm:col-span-3"
                                    options={rooms}
                                    valueAttr="id"
                                    labelAttr="name"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={false}
                                />

                                <h5 className="sm:col-span-6 font-medium text-lg">Kurs</h5>
                                <Select
                                    path="course"
                                    label="Kurs"
                                    className="sm:col-span-3"
                                    options={courses}
                                    valueAttr="id"
                                    labelAttr="form_label"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    isClearable={false}
                                    valueOnly={false}
                                />


                                <h5 className="sm:col-span-6 font-medium text-lg">Info</h5>

                                <p className="sm:col-span-6 text-sm">
                                    <a href={`/summer-school/registration/${data.id}/${data.secret}/edit`}
                                       className="text-imsblue-600" target={"_blank"}>Link zur Ansicht für den
                                        Anmelder</a> <br/>
                                </p>
                                <p className="sm:col-span-6 text-sm">
                                    Status: {data.get_status_display}
                                </p>
                                <p className="sm:col-span-6 text-sm">
                                    Erstellt am {formatDate(data.creation_date)}<br/>
                                    Zuletzt bearbeitet am {formatDate(data.last_update_date)}
                                </p>

                                <div className="sm:col-span-6 flex gap-2 space-between">
                                    <SuccessButton onClick={() => onSetStatus('ACCEPTED')} label="Anmeldung bestätigen"></SuccessButton>
                                    <SecondaryButton onClick={() => onSetStatus('PENDING_CHANGES')} label="Änderungen bestätigen lassen"></SecondaryButton>
                                    <DangerButton onClick={() => onSetStatus('REJECTED')} label="Anmeldung ablehnen"></DangerButton>
                                </div>

                            </>
                        </FormSectionContent>}


                        {currentStep === 14 && <FormSectionContent>
                            <>
                                <div className="sm:col-span-6 flex justify-between">
                                    <h5 className="sm:col-span-6 font-medium text-lg">Rechnung</h5>
                                </div>

                                <div className="sm:col-span-3 pt-6">
                                    <UpdateButton label="Rechnungspositionen neu berechnen" onClick={onRefreshInvoiceLines}/>
                                </div>
                                <DateInput
                                    path="invoice_date"
                                    label="Rechnungsdatum"
                                    className="sm:col-span-3"
                                    onChange={handleDateChange}
                                    errors={errors}
                                    data={data}
                                />

                                <div className="sm:col-span-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-500">
                                                Art
                                            </td>
                                            <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-500">
                                                Titel
                                            </td>
                                            <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-500">
                                                Anzahl
                                            </td>
                                            <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-500">
                                                Preis
                                            </td>
                                            <td className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-500">
                                            </td>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {data.invoice_lines?.map((line, index) => (
                                            <tr key={index}
                                                className={(index % 2 === 0 ? undefined : "bg-gray-50")}
                                            >
                                                <td className="break-word py-4 pl-4 pr-3 text-sm">

                                                    <Select
                                                        path="line_type"
                                                        label="Art"
                                                        className="hide-label"
                                                        options={choices?.invoice_line_types}
                                                        valueAttr="value"
                                                        labelAttr="label"
                                                        isClearable={false}
                                                        valueOnly={true}
                                                        onChange={handleInvoiceLineListChange}
                                                        errors={invoiceLineErrors}
                                                        data={data.invoice_lines}
                                                        rowId={line.id}
                                                    />
                                                </td>
                                                <td className="break-word py-4 pl-4 pr-3 text-sm">
                                                    <Input
                                                        path="title"
                                                        label="Titel"
                                                        className="hide-label"
                                                        onChange={handleInvoiceLineListChange}
                                                        errors={invoiceLineErrors}
                                                        data={data.invoice_lines}
                                                        rowId={line.id}
                                                    />
                                                </td>
                                                <td className="w-12 py-4 pl-4 pr-3 text-sm">
                                                    <Input
                                                        path="quantity"
                                                        type="number"
                                                        label="Anzahl"
                                                        className="hide-label"
                                                        onChange={handleInvoiceLineListChange}
                                                        errors={invoiceLineErrors}
                                                        data={data.invoice_lines}
                                                        rowId={line.id}
                                                    />
                                                </td>
                                                <td className="w-28 py-4 pl-4 pr-3 text-sm">
                                                    <Input
                                                        path="price"
                                                        type="number"
                                                        label="Preis"
                                                        className="hide-label"
                                                        onChange={handleInvoiceLineListChange}
                                                        errors={invoiceLineErrors}
                                                        data={data.invoice_lines}
                                                        rowId={line.id}
                                                    />
                                                </td>
                                                <td className="w-12 py-4 pl-4 pr-3 text-sm">
                                                    <DeleteButton
                                                        iconOnly={true}
                                                        onDelete={() => handleInvoiceLineDelete(line.id)}/>
                                                </td>

                                            </tr>
                                        ))}

                                        </tbody>
                                    </table>

                                </div>

                                <div className="flex justify-end sm:col-span-6">
                                    <div className="my-4 ml-4">
                                        <AddButton onClick={(e) => addInvoiceLineRow(e)} label="Position hinzufügen"/>
                                    </div>
                                </div>

                                <h5 className="sm:col-span-6 font-medium text-lg">Status</h5>

                                <div className="sm:col-span-6 text-sm flex gap-2">

                                    <DownloadButton
                                        apiFunction={() => getInvoicePDF(data.id)}
                                        fileName="Rechnung.pdf"
                                        label="PDF herunterladen"/>

                                    <DownloadButton
                                        apiFunction={() => getInvoicePDF(data.id, true)}
                                        fileName="Rechnung.pdf"
                                        label="PDF neu generieren"/>

                                    <SecondaryButton label="Rechnung per Email senden" onClick={onSendInvoice} />

                                </div>

                                <Checkbox
                                    path="paid"
                                    label="Bezahlt"
                                    className="sm:col-span-3"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                    hideLegend={true}
                                />

                                <DateInput
                                    path="payment_date"
                                    label="Bezahlt am"
                                    className="sm:col-span-3"
                                    onChange={handleDateChange}
                                    errors={errors}
                                    data={data}
                                />

                                {data.invoice_sent_date && <p className="sm:col-span-6 text-sm">
                                    Zuletzt versendet am {formatDate(data.invoice_sent_date)}
                                </p>}
                            </>
                        </FormSectionContent>}

                    </Form>


                    {!isTeamEditing && <div className="flex justify-between">
                        <div>
                            {currentStep > 1 && currentStep < totalAvailableSteps - 1 &&
                                <SecondaryButton onClick={previousStep} label="Zurück"/>}
                        </div>
                        <div className="flex justify-between gap-2">
                            {currentStep < totalAvailableSteps - 1 &&
                                <SecondaryButton onClick={onSave} label="Speichern"/>}
                            {currentStep < totalAvailableSteps - 1 &&
                                <PrimaryButton onClick={nextStep} label="Weiter"/>}
                            {currentStep === totalAvailableSteps - 1 &&
                                <PrimaryButton onClick={nextStep} label="Anmeldung absenden"/>}
                        </div>
                    </div>}

                    {/* Team buttons */}
                    {isTeamEditing && <div className="flex justify-between">
                        <div>
                            {currentStep > 1 && currentStep < totalAvailableSteps &&
                                <SecondaryButton onClick={previousStep} label="Zurück"/>}
                        </div>
                        <div className="flex justify-between gap-2">
                            <SecondaryButton onClick={onSave} label="Speichern"/>
                            {currentStep < totalAvailableSteps - 1 &&
                                <PrimaryButton onClick={nextStep} label="Weiter"/>}
                        </div>
                    </div>}


                </div>

            </div>

        </div>
    )
}
